import { RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { Button } from '@/components/atoms/button';
import { useSession } from '@/features/auth/context/SessionContext';
import { useCreateNotionSyncMutation } from '@/features/zones/services/zones.api';
import { isUnexpectedZonesError, mapZonesApiError } from '@/features/zones/utils/mapZonesApiError';
import { ZONES_MIXPANEL_EVENTS } from '@/features/zones/utils/mixpanelEvents';
import { useMixpanel } from '@/hooks/useMixpanel';
import { notifyError } from '@/libs/bugsnag';

/**
 * The single zone-import entry point: a one-click auto-commit Notion sync,
 * available to any authenticated user. On success it routes to the outcome
 * screen, which streams live progress over SSE; on failure it surfaces a
 * mapped toast.
 */
export function SyncFromNotionButton() {
  const { user } = useSession();
  const navigate = useNavigate();
  const trackAction = useMixpanel((s) => s.trackAction);
  const { mutate, isPending } = useCreateNotionSyncMutation();

  // The admin allowlist was removed; `is_admin` is now true for every
  // authenticated session, so this guards only against an unauthenticated one.
  if (!user?.is_admin) return null;

  const handleClick = () => {
    trackAction(ZONES_MIXPANEL_EVENTS.SYNC_TRIGGERED, { initiated_by_email: user.email });
    mutate(undefined, {
      onSuccess: (res) => {
        navigate(`/zones/imports/${res.job_id}/outcome`);
      },
      onError: (err) => {
        // Report only unexpected failures (5xx / network) to Bugsnag —
        // expected client outcomes (400 bad request, etc.) are user-actionable
        // and surface as a toast, not as monitored errors (Principle VIII).
        if (isUnexpectedZonesError(err)) {
          notifyError(err, { context: 'zones:sync:create', metadata: { source: 'sync' } });
        }
        const presentation = mapZonesApiError(err, { source: 'sync' });
        // 401 is handled globally by the HttpService interceptor (redirect
        // to login); everything else surfaces as a toast here.
        if (presentation.kind === 'toast') toast.error(presentation.message);
      },
    });
  };

  return (
    <Button size="lg" onClick={handleClick} disabled={isPending}>
      <RefreshCw className={isPending ? 'animate-spin' : undefined} aria-hidden />
      {isPending ? 'Syncing…' : 'Sync from Notion'}
    </Button>
  );
}
